import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, GitBranch } from 'lucide-react';
import { Button } from '../ui/button.jsx';
import { cn } from '../../lib/utils';
import { getRelationshipGraphData } from '../../services/api';
import RelationshipGraph from './RelationshipGraph';

/**
 * RelationshipSummary - Embedded summary badge that expands to a full graph visualisation
 *
 * Shows "X relationships" as a compact summary. When clicked, expands to show the
 * interactive @xyflow/react relationship graph.
 *
 * @param {Object} props
 * @param {string} props.entityId - ID of the customer or organisation
 * @param {'customer'|'organisation'} props.entityType - Type of entity
 * @param {Function} [props.onNodeClick] - Callback when a graph node is clicked
 * @param {string} [props.className] - Additional CSS classes
 */
export default function RelationshipSummary({ entityId, entityType, onNodeClick, className }) {
  const [expanded, setExpanded] = useState(false);
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalRelationships, setTotalRelationships] = useState(0);

  useEffect(() => {
    if (!entityId || !entityType) return;

    // Only fetch if expanded, or fetch a lightweight count
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await getRelationshipGraphData(entityId, entityType);
        setGraphData(data);
        // Count edges as the number of relationships
        setTotalRelationships(data.edges?.length || 0);
      } catch (err) {
        console.error('Failed to load relationship graph:', err);
        setGraphData({ nodes: [], edges: [] });
        setTotalRelationships(0);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [entityId, entityType]);

  if (loading) {
    return (
      <div className={cn('text-xs text-muted-foreground animate-pulse', className)}>
        Loading relationships...
      </div>
    );
  }

  if (totalRelationships === 0) return null;

  return (
    <div className={cn('space-y-2', className)}>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs gap-1.5"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <GitBranch className="h-3.5 w-3.5" />
        {totalRelationships} relationship{totalRelationships !== 1 ? 's' : ''}
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </Button>

      {expanded && graphData && (
        <RelationshipGraph
          graphData={graphData}
          onNodeClick={onNodeClick}
        />
      )}
    </div>
  );
}
